import { RobotOutlined } from '@ant-design/icons';

import { Button } from '../../../components/ui';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import type { KetQuaCanTrongSo } from '../types';

export interface HopCanTrongSoProps {
  kq: KetQuaCanTrongSo;
  dangApDung: boolean;
  onApDung: () => void;
  onBo: () => void;
}

/**
 * Xem trước đề xuất trọng số của AI trước khi áp vào bản nháp.
 *
 * Tồn tại vì backend **không ghi gì** (đặc tả mục 13.3): cân trọng số là một đề xuất, và bộ tiêu chí là thứ
 * người soạn chịu trách nhiệm trước cấp trên. Người soạn phải thấy từng dòng `5 → 7` cùng lý do, rồi tự bấm
 * Áp dụng — khác với việc mở màn hình ra và thấy bộ của mình đã khác.
 *
 * Cảnh báo hiện **trước** danh sách đổi: đặc tả mục 2.5 nói im lặng bỏ là lỗi nặng nhất, nên "mô hình bịa 3
 * mã" và "5 tiêu chí giữ nguyên" không được nằm dưới một danh sách dài phải cuộn mới thấy.
 */
export function HopCanTrongSo({ kq, dangApDung, onApDung, onBo }: HopCanTrongSoProps) {
  const { t, locale } = useT();

  return (
    <div className="space-y-3 border-b border-default bg-primary-50 p-3">
      <div className="flex items-start gap-2">
        <RobotOutlined className="mt-0.5 shrink-0 text-primary-600" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-primary-800">
            {t('thamDinh.soan.canKetQua', { so: soVN(kq.da_doi.length, locale) })}
          </p>
          <p className="text-2xs text-content-muted">{t('thamDinh.soan.canModel', { model: kq.model })}</p>
        </div>
      </div>

      {kq.canh_bao.length > 0 && (
        <ul className="space-y-1 rounded border border-warning-200 bg-warning-50 p-2">
          {kq.canh_bao.map((c) => (
            <li key={c} className="text-sm text-warning-800">{c}</li>
          ))}
        </ul>
      )}

      {kq.da_doi.length > 0 && (
        <ul className="max-h-60 space-y-1 overflow-y-auto rounded border border-default bg-surface p-2">
          {kq.da_doi.map((d) => (
            <li key={d.ma} className="flex items-baseline gap-2 text-sm">
              <code className="shrink-0 text-2xs text-content-muted">{d.ma}</code>
              <span className="shrink-0 font-medium">
                {soVN(d.cu, locale)} → {soVN(d.moi, locale)}
              </span>
              {d.ly_do && <span className="min-w-0 flex-1 truncate text-content-muted">{d.ly_do}</span>}
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onBo}>{t('chung.huy')}</Button>
        <Button
          variant="primary"
          size="sm"
          loading={dangApDung}
          disabled={kq.da_doi.length === 0}
          onClick={onApDung}
        >
          {t('thamDinh.soan.canApDung')}
        </Button>
      </div>
    </div>
  );
}
